import React from 'react';
import { Check } from 'lucide-react';
import { Duty, Staff } from '../api';

// スタッフの所属店舗で使われている対応業務。
const dutiesForStaff = (staff: Staff, duties: Duty[]) => duties.filter(d => d.storeIds.some(id => (staff.stores || []).includes(id)));

// 一覧用：対応できる業務の表示。
export const StaffDutyChips: React.FC<{ staff: Staff; duties: Duty[] }> = ({ staff, duties }) => {
  const own = dutiesForStaff(staff, duties).filter(d => (staff.dutyIds || []).includes(d.id));
  if (own.length === 0) return <span className="text-[11px] text-slate-300 font-bold">なし</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {own.map(d => <span key={d.id} className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5 whitespace-nowrap">{d.name}</span>)}
    </div>
  );
};

// 編集用：対応できる業務の選択。
export const StaffDutyEditor: React.FC<{ staff: Staff; duties: Duty[]; onChange: (dutyIds: string[]) => void; labelClass: string }> = ({ staff, duties, onChange, labelClass }) => {
  const available = dutiesForStaff(staff, duties);
  if (available.length === 0) return null;
  const selected = staff.dutyIds || [];
  return (
    <div className="space-y-2" data-testid="staff-duty-editor">
      <label className={labelClass}>対応できる業務</label>
      <div className="flex flex-wrap gap-2">
        {available.map(d => {
          const on = selected.includes(d.id);
          return (
            <button key={d.id} type="button" aria-pressed={on} onClick={() => onChange(on ? selected.filter(id => id !== d.id) : [...selected, d.id])}
              className={`px-3 py-1.5 rounded-[4px] text-xs font-bold border flex items-center gap-1 transition-colors ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400'}`}>
              {on && <Check size={12} />}{d.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// 業務のマーク（未設定は業務名の先頭1文字）。
export const dutyMark = (d: Duty) => (d.mark && d.mark.trim()) || d.name.slice(0, 1);

// 名前の横に付ける対応業務のマーク（例：鍵）。表示する業務を絞る場合は duties に渡す。
export const DutyMarks: React.FC<{ staff?: Staff | null; duties: Duty[]; size?: 'xs' | 'sm' }> = ({ staff, duties, size = 'sm' }) => {
  if (!staff) return null;
  const own = duties.filter(d => (staff.dutyIds || []).includes(d.id));
  if (own.length === 0) return null;
  const cls = size === 'xs' ? 'min-w-[14px] h-[14px] text-[8px] px-[2px]' : 'min-w-[18px] h-[18px] text-[10px] px-[3px]';
  return (
    <span className="inline-flex items-center gap-[2px] align-middle shrink-0">
      {own.map(d => (
        <span key={d.id} title={d.name} aria-label={d.name} className={`inline-flex items-center justify-center rounded-[3px] border border-blue-300 bg-blue-50 text-blue-700 font-bold leading-none whitespace-nowrap ${cls}`}>{dutyMark(d)}</span>
      ))}
    </span>
  );
};
